import { exportToCanvas, exportToSvg } from "../scene/export";
import { ExportType } from "../scene/types";
import {
  copyBlobToClipboardAsPng,
  copyTextToSystemClipboard
} from "../utils/clipboard";
import { DEFAULT_EXPORT_PADDING, isFirefox, MIME_TYPES } from "../utils/constants";
import { getNonDeletedElements } from "../element";
import { t } from "../utils/i18n";
import {
  AppState, BinaryFiles, BinaryFileData, SocketId, UserIdleState,
} from "../utils/types";
import { canvasToBlob, canvasToPngBlob } from "./blob";
import { fileSave, FileSystemHandle } from "./filesystem";
import { serializeAsJSON } from "./json";

import { bytesToHexString, generateMd5 } from "../utils/utils";
import { isSomeElementSelected, getSelectedElements } from "../scene";
import { compressData, decompressData } from "./encode";
import {
  decryptData,
  generateEncryptionKey,
  IV_LENGTH_BYTES
} from "./encryption";
import { restore } from "./restore";
import { ImportedDataState } from "./types";
import { SceneBounds } from "../element/bounds";
import { isInvisiblySmallElement } from "../element/sizeHelpers";
import { isInitializedImageElement } from "../element/typeChecks";
import { FileId } from "../element/types";
import type {
  SchemadrawElement,
  SchemadrawFrameLikeElement,
  NonDeletedSchemadrawElement,
  OrderedSchemadrawElement,
} from "../element/types";
import { isFrameLikeElement } from "../element/typeChecks";
import type { MakeBrand } from "../utils/utility-types";
import {
  TXCOS_STORAGE_PREFIXES,
  DELETED_ELEMENT_TIMEOUT,
  FILE_UPLOAD_MAX_BYTES,
  ROOM_ID_BYTES,
  BACKEND_API_URL,
  DEFAULT_FILENAME,
  WS_SUBTYPES
} from "../utils/constants";
import { encodeFilesForUpload } from "./FileManager";
import { saveFilesToTxcos, saveImageToTxcos } from "./txcos";
import { encodePngMetadata } from "./image";
import { getElementsOverlappingFrame } from "../utils/frame";
import { cloneJSON } from "../utils/utils";

export { loadFromBlob } from "./blob";
export { loadFromJSON, saveAsJSON } from "./json";

import { Buffer } from 'buffer';

export type ExportedElements = readonly NonDeletedSchemadrawElement[] & {
  _brand: "exportedElements";
};

export const prepareElementsForExport = (
  elements: readonly SchemadrawElement[],
  { selectedElementIds }: Pick<AppState, "selectedElementIds">,
  exportSelectionOnly: boolean,
) => {
  elements = getNonDeletedElements(elements);

  const isExportingSelection =
    exportSelectionOnly &&
    isSomeElementSelected(elements, { selectedElementIds });

  let exportingFrame: SchemadrawFrameLikeElement | null = null;
  let exportedElements = isExportingSelection
    ? getSelectedElements(
      elements,
      { selectedElementIds },
      {
        includeBoundTextElement: true,
      },
    )
    : elements;

  if (isExportingSelection) {
    if (
      exportedElements.length === 1 &&
      isFrameLikeElement(exportedElements[0])
    ) {
      exportingFrame = exportedElements[0];
      exportedElements = getElementsOverlappingFrame(elements, exportingFrame);
    } else if (exportedElements.length > 1) {
      exportedElements = getSelectedElements(
        elements,
        { selectedElementIds },
        {
          includeBoundTextElement: true,
          includeElementsInFrames: true,
        },
      );
    }
  }

  return {
    exportingFrame,
    exportedElements: cloneJSON(exportedElements) as ExportedElements,
  };
};

export const exportCanvas = async (
  type: Omit<ExportType, "backend">,
  elements: readonly NonDeletedSchemadrawElement[],
  appState: AppState,
  files: BinaryFiles,
  {
    exportBackground,
    exportPadding = DEFAULT_EXPORT_PADDING,
    viewBackgroundColor,
    name = appState.name || DEFAULT_FILENAME,
    fileId,
    fileHandle = null,
    exportingFrame = null,

  }: {
    exportBackground: boolean;
    exportPadding?: number;
    viewBackgroundColor: string;
    name: string;
    fileId: string;
    fileHandle?: FileSystemHandle | null;
    exportingFrame: SchemadrawFrameLikeElement | null;

  },
) => {
  if (elements.length === 0) {
    throw new Error(t("alerts.cannotExportEmptyCanvas"));
  }

  if (type === "svg" || type === "clipboard-svg") {
    const tempSvg = await exportToSvg(
      elements,
      {
        exportBackground,
        exportWithDarkMode: appState.exportWithDarkMode,
        viewBackgroundColor,
        exportPadding,
        exportScale: appState.exportScale,
        exportEmbedScene: appState.exportEmbedScene && type === "svg",
      },
      files,
      { exportingFrame },
    );
    if (type === "svg") {
      return await fileSave(
        new Blob([tempSvg.outerHTML], { type: MIME_TYPES.svg }),
        {
          description: "Export to SVG",
          name,
          extension: appState.exportEmbedScene ? "schemadraw.svg" : "svg",
          fileHandle,
        },
      );
    } else if (type === "clipboard-svg") {
      await copyTextToSystemClipboard(tempSvg.outerHTML);
      return;
    }
  }

  const tempCanvas = await exportToCanvas(elements, appState, files, {
    exportBackground,
    viewBackgroundColor,
    exportPadding,
    exportingFrame
  });
  tempCanvas.style.display = "none";
  document.body.appendChild(tempCanvas);

  if (type === "png") {
    let blob = await canvasToPngBlob(tempCanvas);
    tempCanvas.remove();
    if (appState.exportEmbedScene) {
      blob = await encodePngMetadata({
        blob,
        metadata: serializeAsJSON(elements, appState, files, "local"),
      });
    }

    return await fileSave(blob, {
      description: "Export to PNG",
      name,
      extension: appState.exportEmbedScene ? "schemadraw.png" : "png",
      fileHandle,
    });
  } else if (type === "clipboard") {
    try {
      const blob = canvasToPngBlob(tempCanvas);
      await copyBlobToClipboardAsPng(blob);
    } catch (error: any) {
      console.warn(error);
      if (error.name === "CANVAS_POSSIBLY_TOO_BIG") {
        throw error;
      }
      // TypeError *probably* suggests ClipboardItem not defined, which
      // people on Firefox can enable through a flag, so let's tell them.
      if (isFirefox && error.name === "TypeError") {
        throw new Error(
          `${t("alerts.couldNotCopyToClipboard")}\n\n${t(
            "hints.firefox_clipboard_write",
          )}`,
        );
      } else {
        throw new Error(t("alerts.couldNotCopyToClipboard"));
      }
    } finally {
      tempCanvas.remove();
    }
  } else if (type == "backend") {
    let fileName = fileId
    let blob = await canvasToBlob(tempCanvas);
    tempCanvas.remove();
    if (appState.exportEmbedScene) {
      blob = await encodePngMetadata({
        blob,
        metadata: serializeAsJSON(elements, appState, files, "local"),
      });
    }
    await saveImageToTxcos({
      prefix: `${TXCOS_STORAGE_PREFIXES.sceneSnapshotImages}`,
      name: fileName, // use fileId to save image
      file: blob,
    })
  } else {
    tempCanvas.remove();
    // shouldn't happen
    throw new Error("Unsupported export type");
  }
};

export type SyncableSchemadrawElement = OrderedSchemadrawElement &
  MakeBrand<"SyncableSchemadrawElement">;

export const isSyncableElement = (
  element: OrderedSchemadrawElement,
): element is SyncableSchemadrawElement => {
  if (element.isDeleted) {
    if (element.updated > Date.now() - DELETED_ELEMENT_TIMEOUT) {
      return true;
    }
    return false;
  }
  return !isInvisiblySmallElement(element);
};

export const getSyncableElements = (
  elements: readonly OrderedSchemadrawElement[],
) =>
  elements.filter((element) =>
    isSyncableElement(element),
  ) as SyncableSchemadrawElement[];

const generateRoomId = async () => {
  const buffer = new Uint8Array(ROOM_ID_BYTES);
  window.crypto.getRandomValues(buffer);
  return bytesToHexString(buffer);
};

/**
 * Right now the reason why we resolve connection params (url, polling...)
 * from upstream is to allow changing the params immediately when needed without
 * having to wait for clients to update the SW.
 *
 * If REACT_APP_WS_SERVER_URL env is set, we use that instead (useful for forks)
 */
export const getCollabServer = async (): Promise<{
  url: string;
  polling: boolean;
}> => {
  if ("local") {
    return {
      url: "local",
      polling: true,
    };
  }

  try {
    const resp = await fetch(
      `local/collab-server`,
    );
    return await resp.json();
  } catch (error) {
    console.error(error);
    throw new Error(t("errors.cannotResolveCollabServer"));
  }
};
export type EncryptedData = {
  data: ArrayBuffer;
  iv: Uint8Array;
};

export type SocketUpdateDataSource = {
  INVALID_RESPONSE: {
    type: WS_SUBTYPES.INVALID_RESPONSE;
  };
  SCENE_INIT: {
    type: WS_SUBTYPES.INIT;
    payload: {
      elements: readonly SchemadrawElement[];
    };
  };
  SCENE_UPDATE: {
    type: WS_SUBTYPES.UPDATE;
    payload: {
      elements: readonly SchemadrawElement[];
    };
  };
  MOUSE_LOCATION: {
    type: WS_SUBTYPES.MOUSE_LOCATION;
    payload: {
      socketId: SocketId;
      pointer: { x: number; y: number; tool: "pointer" | "laser" };
      button: "down" | "up";
      selectedElementIds: AppState["selectedElementIds"];
      username: string;
    };
  };
  USER_VISIBLE_SCENE_BOUNDS: {
    type: WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS;
    payload: {
      socketId: SocketId;
      username: string;
      sceneBounds: SceneBounds;
    };
  };
  IDLE_STATUS: {
    type: WS_SUBTYPES.IDLE_STATUS;
    payload: {
      socketId: SocketId;
      userState: UserIdleState;
      username: string;
    };
  };
};

export type SocketUpdateDataIncoming =
  SocketUpdateDataSource[keyof SocketUpdateDataSource];

export type SocketUpdateData =
  SocketUpdateDataSource[keyof SocketUpdateDataSource] & {
    _brand: "socketUpdateData";
  };

const RE_COLLAB_LINK = /^#room=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/;

export const isCollaborationLink = (link: string) => {
  const hash = new URL(link).hash;
  return RE_COLLAB_LINK.test(hash);
};

export const getCollaborationLinkData = (link: string) => {
  const hash = new URL(link).hash;
  const match = hash.match(RE_COLLAB_LINK);
  if (match && match[2].length !== 22) {
    window.alert(t("alerts.invalidEncryptionKey"));
    return null;
  }
  return match ? { roomId: match[1], roomKey: match[2] } : null;
};

export const generateCollaborationLinkData = async () => {
  const roomId = await generateRoomId();
  const roomKey = await generateEncryptionKey();

  if (!roomKey) {
    throw new Error("Couldn't generate room key");
  }

  return { roomId, roomKey };
};

export const getCollaborationLink = (data: {
  roomId: string;
  roomKey: string;
}) => {
  return `${window.location.origin}${window.location.pathname}#room=${data.roomId},${data.roomKey}`;
};

/**
 * Decodes shareLink data using the legacy buffer format.
 * @deprecated
 */
const legacy_decodeFromBackend = async ({
  buffer,
  decryptionKey,
}: {
  buffer: ArrayBuffer;
  decryptionKey: string;
}) => {
  let decrypted: ArrayBuffer;

  try {
    // Buffer should contain both the IV (fixed length) and encrypted data
    const iv = buffer.slice(0, IV_LENGTH_BYTES);
    const encrypted = buffer.slice(IV_LENGTH_BYTES, buffer.byteLength);
    decrypted = await decryptData(new Uint8Array(iv), encrypted, decryptionKey);
  } catch (error: any) {
    // Fixed IV (old format, backward compatibility)
    const fixedIv = new Uint8Array(IV_LENGTH_BYTES);
    decrypted = await decryptData(fixedIv, buffer, decryptionKey);
  }

  // We need to convert the decrypted array buffer to a string
  const string = new window.TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  const data: ImportedDataState = JSON.parse(string);

  return {
    elements: data.elements || null,
    appState: data.appState || null,
  };
};

const importFromTemplate = async (
  id: string,
  decryptionKey: string | null,
): Promise<ImportedDataState> => {
  let url = ""
  if (decryptionKey != "") {
    url = `${BACKEND_API_URL.TEMPLATE_GET_URL}?id=${id}`
  } else {
    url = `${BACKEND_API_URL.TEMPLATE_GET_URL}?id=${id}&key=${decryptionKey}`
  }

  return getFromBackend(url);
}

const importFromDesign = async (
  id: string,
  decryptionKey: string | null,
): Promise<ImportedDataState> => {
  let url = ""
  if (decryptionKey != "") {
    url = `${BACKEND_API_URL.DESIGN_GET_URL}?id=${id}`
  } else {
    url = `${BACKEND_API_URL.DESIGN_GET_URL}?id=${id}&key=${decryptionKey}`
  }

  return getFromBackend(url);
}

const getFromBackend = async (
  url: string
): Promise<ImportedDataState> => {
  try {
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',// 避免cors攻击
      credentials: 'include'
    });

    if (!response.ok) {
      window.alert(t("alerts.importBackendFailed"));
      return {};
    }
    //const buffer = await response.arrayBuffer();

    try {
      let code = 0;
      let id = "";
      let contentStr = "";
      let decryptionKey = "";
      await response.json().then(function (data) {
        code = data.code
        id = data.info.id
        decryptionKey = data.info.encryptionKey
        contentStr = data.info.content
      });

      if (code == 1002) {
        window.alert(t("relogonConfirm.description"));
        window.open("/login?from=" + document.URL, "_self");
        return {}
      }
      const { data: decodedBuffer } = await decompressData(
        Buffer.from(contentStr.split(',').map(letter => parseInt(letter))),
        {
          decryptionKey,
        },
      );
      const content: ImportedDataState = JSON.parse(
        new TextDecoder().decode(decodedBuffer),
      );

      let appState = {
        ...content.appState,
        id: id,
        privateKey: decryptionKey,
        contentMd5: content.appState?.contentMd5
      }

      return {
        elements: content.elements || null,
        appState: appState || null,
      };
    } catch (error: any) {
      console.warn(
        "error when decoding response data using the new format:",
        error,
      );
      return {};
    }
  } catch (error: any) {
    window.alert(t("alerts.importBackendFailed"));
    console.error(error);
    return {};
  }
};

export const loadScene = async (
  id: string | null,
  privateKey: string | null,
  // Supply local state even if importing from backend to ensure we restore
  // localStorage user settings which we do not persist on server.
  // Non-optional so we don't forget to pass it even if `undefined`.
  localDataState: ImportedDataState | undefined | null,
) => {
  let data;
  if (id != null) {
    // the private key is used to decrypt the content from the server, take
    // extra care not to leak it
    data = restore(
      await importFromDesign(id, privateKey),
      localDataState?.appState,
      localDataState?.elements,
      { repairBindings: true, refreshDimensions: false },
    );
  } else {
    data = restore(localDataState || null, null, null);
  }

  return {
    elements: data.elements,
    appState: data.appState,
    // note: this will always be empty because we're not storing files
    // in the scene database/localStorage, and instead fetch them async
    // from a different database
    files: data.files,
    commitToHistory: false,
  };
};

export const loadTemplate = async (
  id: string | null,
  privateKey: string | null,
  // Supply local state even if importing from backend to ensure we restore
  // localStorage user settings which we do not persist on server.
  // Non-optional so we don't forget to pass it even if `undefined`.
  localDataState: ImportedDataState | undefined | null,
) => {
  let data;
  if (id != null) {
    // the private key is used to decrypt the content from the server, take
    // extra care not to leak it
    data = restore(
      await importFromTemplate(id, privateKey),
      localDataState?.appState,
      localDataState?.elements,
    );
  } else {
    data = restore(localDataState || null, null, null, {
      repairBindings: true,
    });
  }

  return {
    elements: data.elements,
    appState: data.appState,
    // note: this will always be empty because we're not storing files
    // in the scene database/localStorage, and instead fetch them async
    // from a different database
    files: data.files,
  };
};

type CreateToBackendResult =
  | { id: null; privateKey: null, md5: null, errorMessage: string }
  | { id: string; privateKey: string, md5: string, errorMessage: null };

type UpdateToBackendResult =
  | { md5: null, errorMessage: string }
  | { md5: string, errorMessage: null };

type ShareToBackendResult =
  | { url: null; md5: null, errorMessage: string }
  | { url: string; md5: string, errorMessage: null };

export const createToBackend = async (
  elements: readonly SchemadrawElement[],
  appState: AppState,
  files: BinaryFiles,
): Promise<CreateToBackendResult> => {
  // no data
  if (elements.length === 0) {
    return { id: null, privateKey: null, md5: null, errorMessage: "" };
  }

  const encryptionKey = await generateEncryptionKey("string");
  const md5Str = generateMd5(JSON.stringify({ elements, files }))
  appState.contentMd5 = md5Str;

  const payload = await compressData(
    new TextEncoder().encode(
      serializeAsJSON(elements, appState, files, "database"),
    ),
    { encryptionKey },
  );

  try {
    const filesMap = new Map<FileId, BinaryFileData>();
    for (const element of elements) {
      if (isInitializedImageElement(element) && files[element.fileId]) {
        filesMap.set(element.fileId, files[element.fileId]);
      }
    }

    const filesToUpload = await encodeFilesForUpload({
      files: filesMap,
      encryptionKey,
      maxBytes: FILE_UPLOAD_MAX_BYTES,
    });

    const body = {
      id: appState.id,
      category: "白板",
      title: appState.name,
      encryptionKey: encryptionKey,
      content: payload.toString(),
    }

    const response = await fetch(BACKEND_API_URL.DESIGN_CREATE_URL, {
      method: "POST",
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const json = await response.json();
    if (json.code === 0) {
      const url = new URL(window.location.href);
      // We need to store the key (and less importantly the id) as hash instead
      // of queryParam in order to never send it to the server
      let id = json.info;
      url.hash = `json=${id},${encryptionKey}`;
      const urlString = url.toString();

      await exportCanvas('backend', elements, appState, files, { ...appState, exportingFrame: null, fileId: id })
      await saveFilesToTxcos({
        prefix: `${TXCOS_STORAGE_PREFIXES.shareLinkFiles}/${id}`,
        files: filesToUpload,
      });

      return { id: id, privateKey: encryptionKey, md5: md5Str, errorMessage: null };
    } else if (json.code === 1002) {
      return {
        id: null, privateKey: null, md5: null, errorMessage: t("relogonConfirm.description"),
      };
    }

    return { id: null, privateKey: null, md5: null, errorMessage: t("alerts.couldNotCreateScene") };
  } catch (error: any) {
    console.error(error);
    return { id: null, privateKey: null, md5: null, errorMessage: t("alerts.couldNotCreateScene") };
  }
};

export const updateToBackend = async (
  elements: readonly SchemadrawElement[],
  appState: AppState,
  files: BinaryFiles,
): Promise<UpdateToBackendResult> => {
  if (!appState.id) {
    return { md5: null, errorMessage: t("alerts.invalidSceneID") };
  }

  // no data
  if (elements.length === 0) {
    return { md5: null, errorMessage: "" };
  }
  // no change

  try {
    const md5Str = generateMd5(JSON.stringify({ elements, files }));
    if (md5Str == appState.contentMd5) {
      return { md5: null, errorMessage: "" };
    } else {
      appState.contentMd5 = md5Str;
    }
    const encryptionKey = appState.privateKey ? appState.privateKey : await generateEncryptionKey("string");
    const id = appState.id ? appState.id : "undefined"

    const payload = await compressData(
      new TextEncoder().encode(
        serializeAsJSON(elements, appState, files, "database"),
      ),
      { encryptionKey },
    );

    const filesMap = new Map<FileId, BinaryFileData>();
    for (const element of elements) {
      if (isInitializedImageElement(element) && files[element.fileId]) {
        filesMap.set(element.fileId, files[element.fileId]);
      }
    }

    const filesToUpload = await encodeFilesForUpload({
      files: filesMap,
      encryptionKey,
      maxBytes: FILE_UPLOAD_MAX_BYTES,
    });

    const body = {
      id: id,
      category: "白板",
      title: appState.name,
      encryptionKey: encryptionKey,
      content: payload.toString(),
    }

    const response = await fetch(BACKEND_API_URL.DESIGN_UPDATE_URL, {
      method: "POST",
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const json = await response.json();
    if (json.code === 0) {
      // save to snapshot
      await exportCanvas('backend', elements, appState, files, { ...appState, exportingFrame: null, fileId: id })
      // save all image on the scene
      await saveFilesToTxcos({
        prefix: `${TXCOS_STORAGE_PREFIXES.shareLinkFiles}/${id}`,
        files: filesToUpload,
      });

      return { md5: md5Str, errorMessage: null };
    } else if (json.error_class === "RequestTooLargeError") {
      return { md5: null, errorMessage: t("alerts.couldNotCreateSceneTooBig") };
    }

    return { md5: null, errorMessage: t("alerts.couldNotCreateScene") };
  } catch (error: any) {
    console.error(error);
    return { md5: null, errorMessage: t("alerts.couldNotCreateScene") };
  }
};

export const shareToBackend = async (
  elements: readonly SchemadrawElement[],
  appState: AppState,
  files: BinaryFiles,
): Promise<ShareToBackendResult> => {
  let idStr = null
  let md5Str = null
  let privateKeyStr = null
  if (appState.id) {
    const { md5, errorMessage } = await updateToBackend(elements, appState, files);
    if (errorMessage) {
      return { url: null, md5: null, errorMessage: errorMessage }
    }
    idStr = appState.id;
    privateKeyStr = appState.privateKey
    md5Str = md5;
  } else {
    const { id, privateKey, md5, errorMessage } = await createToBackend(elements, appState, files);
    if (errorMessage) {
      return { url: null, md5: null, errorMessage: errorMessage }
    }
    idStr = id;
    privateKeyStr = privateKey;
    md5Str = md5;
  }

  const url = new URL(window.location.host + window.location.pathname);
  // We need to store the key (and less importantly the id) as hash instead
  // of queryParam in order to never send it to the server
  url.hash = `json=${idStr},${privateKeyStr}`;
  const urlString = url.toString();

  return { url: urlString, md5: `${md5Str}`, errorMessage: null };
};